import React, { createContext, useContext, ReactNode } from 'react';
import toast, { ToastOptions } from 'react-hot-toast';

interface ToastContextType {
  showSuccess: (message: string, options?: ToastOptions) => void;
  showError: (message: string, options?: ToastOptions) => void;
  showInfo: (message: string, options?: ToastOptions) => void;
  showWarning: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const defaultOptions: ToastOptions = {
    duration: 4000,
    position: 'top-right',
    style: {
      background: '#363636',
      color: '#fff',
      fontSize: '14px',
      padding: '16px',
      borderRadius: '8px',
    },
  };

  const showSuccess = (message: string, options?: ToastOptions) => {
    toast.success(message, {
      ...defaultOptions,
      ...options,
      style: {
        ...defaultOptions.style,
        background: '#4caf50',
        ...options?.style,
      },
    });
  };

  const showError = (message: string, options?: ToastOptions) => {
    toast.error(message, {
      ...defaultOptions,
      ...options,
      style: {
        ...defaultOptions.style,
        background: '#f44336',
        ...options?.style,
      },
    });
  };

  const showInfo = (message: string, options?: ToastOptions) => {
    toast(message, {
      ...defaultOptions,
      ...options,
      style: {
        ...defaultOptions.style,
        background: '#2196f3',
        ...options?.style,
      },
    });
  };

  const showWarning = (message: string, options?: ToastOptions) => {
    toast(message, {
      ...defaultOptions,
      ...options,
      style: {
        ...defaultOptions.style,
        background: '#ff9800',
        ...options?.style,
      },
    });
  };

  return (
    <ToastContext.Provider value={{ showSuccess, showError, showInfo, showWarning }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}; 